import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Camera, 
  Edit3, 
  Save, 
  X, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Globe, 
  Instagram,
  Twitter,
  Linkedin,
  Facebook,
  Youtube,
  Twitch,
  Check,
  AlertCircle
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';
import '../../styles/ArtistProfilePage.css';

const ArtistProfilePage = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState({});
  const [tempValues, setTempValues] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;

      setUser(authUser);

      // Get user profile data
      const { data: profileData, error } = await supabase
        .from('users')
        .select('*')
        .eq('uuid', authUser.id)
        .single();

      if (error) {
        console.error('Error loading profile:', error);
        return;
      }

      setProfile(profileData);
      setTempValues(profileData);
    } catch (error) {
      console.error('Error loading user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (type, file) => {
    try {
      setUploadingImage(true);
      setMessage({ type: '', text: '' });

      const uploadResult = await uploadToCloudinary(file, 'image', 'profiles');
      const imageUrl = uploadResult.url;
      
      if (type === 'profile') {
        await updateProfile({ profile_img: imageUrl });
      } else if (type === 'cover') {
        await updateProfile({ cover_image: imageUrl });
      }

      setMessage({ type: 'success', text: `${type === 'profile' ? 'Profile' : 'Cover'} image updated successfully!` });
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage({ type: 'error', text: 'Failed to upload image. Please try again.' });
    } finally {
      setUploadingImage(false);
    }
  };

  const updateProfile = async (updates) => {
    try {
      const { error } = await supabase
        .from('users')
        .update(updates)
        .eq('uuid', user.id);

      if (error) throw error;

      // Update local state
      setProfile(prev => ({ ...prev, ...updates }));
      setTempValues(prev => ({ ...prev, ...updates }));
      
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      return false;
    }
  };

  const handleSave = async (field) => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const value = tempValues[field];
      if (!value || value === profile[field]) {
        setEditMode(prev => ({ ...prev, [field]: false }));
        return;
      }

      const success = await updateProfile({ [field]: value });
      
      if (success) {
        setEditMode(prev => ({ ...prev, [field]: false }));
        setMessage({ type: 'success', text: `${field.replace('_', ' ')} updated successfully!` });
      } else {
        setMessage({ type: 'error', text: `Failed to update ${field.replace('_', ' ')}. Please try again.` });
      }
    } catch (error) {
      console.error('Error saving:', error);
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (field) => {
    setTempValues(prev => ({ ...prev, [field]: profile[field] }));
    setEditMode(prev => ({ ...prev, [field]: false }));
  };

  const startEdit = (field) => {
    setEditMode(prev => ({ ...prev, [field]: true }));
    setTempValues(prev => ({ ...prev, [field]: profile[field] || '' }));
  };

  const renderEditableField = (field, label, type = 'text', placeholder = '') => {
    const isEditing = editMode[field];
    const value = tempValues[field] || '';

    return (
      <div className="profile-field">
        <label className="profile-field-label">{label}</label>
        <div className="profile-field-content">
          {isEditing ? (
            <div className="edit-mode">
              {type === 'textarea' ? (
                <textarea
                  value={value}
                  onChange={(e) => setTempValues(prev => ({ ...prev, [field]: e.target.value }))}
                  placeholder={placeholder}
                  className="profile-input"
                  data-field={field}
                  rows={4}
                />
              ) : (
                <input
                  type={type}
                  value={value}
                  onChange={(e) => setTempValues(prev => ({ ...prev, [field]: e.target.value }))}
                  placeholder={placeholder}
                  className="profile-input"
                  data-field={field}
                />
              )}
              <div className="edit-actions">
                <button
                  onClick={() => handleSave(field)}
                  disabled={saving}
                  className="save-btn"
                >
                  <Save size={16} color="#ffffff" />
                </button>
                <button
                  onClick={() => handleCancel(field)}
                  className="cancel-btn"
                >
                  <X size={16} color="#ffffff" />
                </button>
              </div>
            </div>
          ) : (
            <div className="view-mode">
              <span className="profile-value">
                {profile[field] || 'Not set'}
              </span>
              <button
                onClick={() => startEdit(field)}
                className="edit-btn"
              >
                <Edit3 size={16} color="#6c757d" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderImageSection = (type, currentImage, title) => (
    <div className="image-upload-section">
      <h3 className="image-section-title">{title}</h3>
      <div className="image-container">
        {currentImage ? (
          <img 
            src={currentImage} 
            alt={title}
            className={`${type}-image`}
          />
        ) : (
          <div className={`${type}-placeholder`}>
            <Camera size={32} color="#6c757d" />
            <span>No {type} image</span>
          </div>
        )}
        <div className="image-overlay">
          <label className="upload-btn">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) handleImageUpload(type, file);
              }}
              style={{ display: 'none' }}
            />
            <Camera size={20} color="#ffffff" />
            {uploadingImage ? 'Uploading...' : 'Change'}
          </label>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading your profile...</p>
      </div>
    );
  }

  return (
    <div className="artist-profile-page">
      {/* Message Display */}
      {message.text && (
        <div className={`message ${message.type}`}>
          <div className="message-content">
            {message.type === 'success' ? <Check size={16} color="#28a745" /> : <AlertCircle size={16} color="#28a745" />}
            <span>{message.text}</span>
          </div>
          <button 
            onClick={() => setMessage({ type: '', text: '' })}
            className="message-close"
          >
            <X size={16} color="#ffffff" />
          </button>
        </div>
      )}

      {/* Cover Image Section - Full Width */}
      <div className="cover-image-section">
        {renderImageSection('cover', profile.cover_image, 'Cover Image')}
      </div>

      {/* Profile Basic Info - Overlapping Cover Image */}
      <div className="profile-basic-info">
        <div className="profile-image-section">
          {renderImageSection('profile', profile.profile_img, 'Profile Image')}
        </div>
        
        <div className="basic-info-fields">
          {renderEditableField('nickname', 'Public Name', 'text', 'Enter your public name')}
          {renderEditableField('handle', 'Handle', 'text', '@yourhandle')}
          {renderEditableField('bio', 'Bio', 'textarea', 'Tell us about yourself...')}
        </div>
      </div>

      {/* Profile Details Section */}
      <div className="profile-details-section">
        <h2 className="section-title">Profile Details</h2>
        
        <div className="profile-fields-grid">
          {renderEditableField('first_name', 'First Name', 'text', 'Enter your first name')}
          {renderEditableField('last_name', 'Last Name', 'text', 'Enter your last name')}
          {renderEditableField('email', 'Email', 'email', 'Enter your email')}
          {renderEditableField('phone', 'Phone', 'tel', 'Enter your phone number')}
          {renderEditableField('location', 'Location', 'text', 'Enter your city/state')}
          {renderEditableField('website', 'Website', 'url', 'https://yourwebsite.com')}
        </div>
      </div>

      {/* Social Media Section */}
      <div className="social-media-section">
        <h2 className="section-title">Social Media</h2>
        
        <div className="social-fields-grid">
          {renderEditableField('instagram', 'Instagram', 'text', '@username')}
          {renderEditableField('twitter', 'Twitter', 'text', '@username')}
          {renderEditableField('linkedin', 'LinkedIn', 'text', 'profile-url')}
          {renderEditableField('facebook', 'Facebook', 'text', 'profile-url')}
          {renderEditableField('youtube', 'YouTube', 'text', 'channel-url')}
          {renderEditableField('twitch', 'Twitch', 'text', 'channel-name')}
        </div>
      </div>

      {/* Account Settings Section */}
      <div className="account-settings-section">
        <h2 className="section-title">Account Settings</h2>
        
        <div className="settings-grid">
          <div className="setting-item">
            <div className="setting-info">
              <h3>Email Notifications</h3>
              <p>Receive updates about your artwork and submissions</p>
            </div>
            <label className="toggle-switch">
              <input type="checkbox" defaultChecked />
              <span className="toggle-slider"></span>
            </label>
          </div>
          
          <div className="setting-item">
            <div className="setting-info">
              <h3>Public Profile</h3>
              <p>Make your profile visible to collectors and galleries</p>
            </div>
            <label className="toggle-switch">
              <input type="checkbox" defaultChecked />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistProfilePage;
